import React from 'react';
import {
  BuildingOffice2Icon,
  ShieldCheckIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  DocumentCheckIcon,
  FingerPrintIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { IndustryPageTemplate } from '../../components/page-templates/MarketPageTemplates';

const challenges = [
  {
    icon: DocumentCheckIcon,
    title: 'Regulatory Compliance',
    description: 'Strict regulatory requirements for ICT equipment imports across multiple financial jurisdictions.',
  },
  {
    icon: LockClosedIcon,
    title: 'Data Security & Chain of Custody',
    description: 'Sensitive financial data demands rigorous chain-of-custody controls throughout the logistics process.',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'High-Value Equipment',
    description: 'Trading floor hardware, servers, and network gear requiring secure, insured handling at every stage.',
  },
  {
    icon: BuildingOffice2Icon,
    title: 'Multi-Branch Deployments',
    description: 'Coordinating ICT rollouts across hundreds of branches and offices in different regulatory jurisdictions.',
  },
];

const solutions = [
  {
    icon: ShieldCheckIcon,
    title: 'IOR/EOR for Regulated Markets',
    description: 'Compliance expertise tailored to financial services regulations across 140+ countries.',
  },
  {
    icon: FingerPrintIcon,
    title: 'Certified Data Wiping',
    description: 'NIST 800-88 and ADISA-certified data destruction with full audit trail documentation.',
  },
  {
    icon: TruckIcon,
    title: 'White Glove Delivery',
    description: 'Secure, handled-with-care delivery for sensitive equipment with proof of delivery and sign-off.',
  },
  {
    icon: EyeIcon,
    title: 'Full Chain-of-Custody Tracking',
    description: 'Real-time visibility from warehouse to desk with tamper-evident packaging and tracking.',
  },
  {
    icon: LockClosedIcon,
    title: 'Secure Warehousing',
    description: 'Access-controlled facilities with CCTV, restricted entry, and inventory segregation.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'Compliance Documentation',
    description: 'Complete import/export documentation and audit-ready records for regulatory inspections.',
  },
];

const stats = [
  { value: '140+', label: 'Countries' },
  { value: '6', label: 'Global Offices' },
  { value: '10+', label: 'Years Experience' },
  { value: '6,500+', label: 'Field Workers' },
];

const FinancialServices: React.FC = () => (
  <IndustryPageTemplate
    eyebrow="Financial Services"
    title="Secure ICT Logistics for Financial Services"
    highlight="Financial Services"
    description="Compliance-first logistics for banks, insurers, and fintech companies managing sensitive ICT infrastructure across regulated markets."
    heroIcon={ShieldCheckIcon}
    heroAccentIcon={LockClosedIcon}
    challengesIntro="Financial institutions face unique logistics demands driven by regulation, security, and operational risk."
    challenges={challenges}
    solutionsIntro="Compliance, security, and chain-of-custody controls designed for regulated financial environments."
    solutions={solutions}
    useCase="From trading floor hardware refreshes to branch network rollouts, we provide the compliance confidence and operational precision that financial institutions demand."
    stats={stats}
    ctaTitle="Ready to secure your ICT logistics operations?"
    ctaDescription="Let TrueLog deliver the compliance confidence and operational precision your financial institution demands."
  />
);

export default FinancialServices;
